import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { createReportURL } from 'core/routes';
import { useTotalLoss, convertToDate } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { AxiosError } from 'axios';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { columns, TOTAL_LOSS_REPORT, TOTAL_LOSS_REPORT_INFO, TOTAL_LOSS_REPORT_TITLE } from './constants';
import { ReportSearchForm } from 'components/ReportForms/ReportSearchForm';
import { useReportParams } from 'pages/ReportsDetails/hooks';
import { ReportProps } from 'pages/ReportsDetails/types';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import DevExtremeReportGrid from 'components/ReportGrid_New';
import { TotalLossReport } from 'api/resources/models/AutoGenerated';

export function TotalLoss({
  reportPath,
  setErrorMessage,
  setToast,
}: ReportProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useReportParams(location.search);
  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParams.startDate) || null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParams.endDate) || null
  );

  const queryEnabled = !!urlParams.startDate && !!urlParams.endDate;
  const { reportData, refetchReport, isReportFetching } = useTotalLoss(
    {
      startDate: urlParams.startDate,
      endDate: urlParams.endDate,
    },
    queryEnabled,
    handleError
  );

  const rows = useMemo<TotalLossReport[] | undefined>(() => reportData, [reportData]);
  useEffect(() => {
    navigateToReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.startDate, urlParams.endDate]);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDate(new Date())} for period ${formatDate(startDate)} to ${formatDate(endDate)}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={TOTAL_LOSS_REPORT_TITLE}
          infoMessage={TOTAL_LOSS_REPORT_INFO}
        />
        <ReportSearchForm
          handleSubmit={handleSubmit}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isDisabled={isReportFetching}
        />
      </UpperInfoContainer>
      {!isReportFetching && rows && rows?.length === 0 && <NoReportDataMessage />}
      <ReportGridContainer direction="column">
        <DevExtremeReportGrid
          columns={columns}
          displayGroupingPanel
          headerTitleForSheet={TOTAL_LOSS_REPORT_TITLE}
          isLoading={isReportFetching}
          pdfOrientation='l'
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={TOTAL_LOSS_REPORT}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParams.startDate == dateToString(startDate) &&
      urlParams.endDate == dateToString(endDate)
    )
      return refetchReport();
    else navigateToReport();
  }

  function navigateToReport() {
    if (!!startDate || !!endDate)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
          },
          reportPath,
        ), { replace: true });
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports('TL Engineering Schedule Report'));
    setToast(true);
  }
}
